import { z } from 'zod';
import { PublicHttpUrl } from '../_shared/url.js';
/**
 * Las redes sociales del medio: qué perfil tiene en cada red del catálogo, en qué orden y cuáles
 * salen al aire.
 *
 * **El catálogo es fijo.** El medio elige entre las redes que la plataforma cargó en
 * `social_networks`; no puede agregar una nueva. Por eso no hay alta ni baja de redes acá: solo
 * asignar URL, activar/desactivar y ordenar. Una red nueva se suma al catálogo junto con su ícono,
 * que es trabajo del equipo.
 *
 * Consecuencia para la pantalla: un medio sin nada cargado ve igual todas las redes del catálogo
 * con el campo vacío, no una lista vacía con un botón "agregar".
 */
/** Tope duro de la lista. El catálogo hoy tiene cinco; el margen es para no volver acá. */
export const REDES_MAX = 20;
/**
 * Tope de la URL. La columna no tiene límite, pero este valor viaja en el cascarón de **toda**
 * respuesta pública: un link pegado con media pantalla de parámetros de tracking se paga en cada
 * página del sitio.
 */
export const RED_URL_MAX = 500;
/**
 * La dirección del perfil.
 *
 * `PublicHttpUrl` no es una elección de estilo: el pie del sitio pone este valor tal cual en un
 * `href`, y `z.string().url()` a secas acepta `javascript:alert(1)`. Es el único campo de esta
 * pantalla que puede terminar en XSS almacenado.
 */
export const UrlDeRed = z.string().trim().min(1).max(RED_URL_MAX).pipe(PublicHttpUrl);
/**
 * La fila de correo del catálogo. No es una red social: es una dirección de contacto que se muestra
 * en la misma tira del pie, con el mismo tratamiento de ícono, orden y encendido.
 */
export const RED_EMAIL_KEY = 'email';
const PREFIJO_CORREO = 'mailto:';
/** La dirección sin el esquema, esté o no. Para validarla siempre igual. */
function direccionPelada(valor) {
    return valor.toLowerCase().startsWith(PREFIJO_CORREO) ? valor.slice(PREFIJO_CORREO.length) : valor;
}
/**
 * La dirección de correo. **Se acepta con o sin `mailto:` y siempre sale con él.**
 *
 * Lo guardado tiene que ser lo que va en el `href` del pie: mientras esa regla se cumpla, hay **un
 * solo** lugar donde mirar para saber que ahí no entra un `javascript:`. Pero exigirle el esquema a
 * quien completa el formulario sería mover ese detalle al que menos tiene que saberlo — nadie tipea
 * `mailto:`. Así que la normalización es parte del contrato y no del panel: cualquier cliente de la
 * API puede mandar `contacto@medio.com` y lo que se guarda es `mailto:contacto@medio.com`.
 *
 * Es seguro porque el orden importa: primero se valida como **dirección de correo estricta** y solo
 * después se le pone el esquema. Un `javascript:alert(1)` no es una dirección válida, así que nunca
 * llega a la parte que concatena.
 *
 * No se reusa `PublicHttpUrl` con un esquema más: ensancharlo aflojaría la validación de **todas**
 * las redes para resolver el caso de una. Son dos validadores y cada clave usa el suyo (`valorDeRed`).
 *
 * Sin parámetros: `mailto:` admite `?subject=`, pero eso es superficie para que alguien arme un
 * mensaje pre-escrito desde el pie de un diario. Solo la dirección.
 */
export const MailtoDeRed = z
    .string()
    .trim()
    .min(1)
    .max(RED_URL_MAX)
    .refine((v) => z.string().email().safeParse(direccionPelada(v)).success, {
    message: 'No parece una dirección de correo',
})
    .transform((v) => `${PREFIJO_CORREO}${direccionPelada(v)}`);
/**
 * Con qué se valida el valor de una fila, según su clave. El catálogo es fijo, así que la lista de
 * excepciones también: hoy es una sola.
 */
export function valorDeRed(key) {
    return key === RED_EMAIL_KEY ? MailtoDeRed : UrlDeRed;
}
/**
 * Una fila de la pantalla: la red del catálogo más lo que el medio cargó, si cargó algo.
 *
 * Se identifica por `key` y no por el id de la fila de `tenant_social_links`: para una red sin
 * cargar esa fila no existe todavía, y el panel necesita poder nombrarla igual.
 */
export const RedDelMedio = z
    .object({
    key: z.string(),
    name: z.string(),
    /** Color de marca del catálogo, para que la fila se reconozca de un vistazo. */
    brandColor: z.string().nullable(),
    /** `null` = el medio no la cargó. No hay fila en la base. */
    url: z.string().nullable(),
    isActive: z.boolean(),
})
    .strict();
export const RedesDelMedioResponse = z.object({ redes: z.array(RedDelMedio) }).strict();
/**
 * Se guarda **la lista completa**: el orden es el del array y lo que no viene se borra.
 *
 * Es el mismo criterio que el orden de la portada y el de una categoría, y acá vale doble: las
 * redes viajan en el `shell`, así que cada escritura obliga a purgar el caché de todo el sitio. Un
 * PUT es una purga; un CRUD por red serían cuatro caminos y cuatro oportunidades de olvidarse.
 *
 * Una fila con el campo de URL vacío no se manda: no existe "red cargada sin URL".
 */
export const GuardarRedesRequest = z
    .object({
    redes: z
        .array(z
        .object({
        key: z.string().min(1),
        // El recorte y el tope valen para cualquier fila; **qué** es un valor válido depende de
        // la clave y se resuelve abajo, para poder marcar el error en la fila que lo tiene.
        url: z.string().trim().min(1).max(RED_URL_MAX),
        isActive: z.boolean(),
    })
        .strict())
        .max(REDES_MAX)
        .superRefine((redes, ctx) => {
        redes.forEach((red, i) => {
            const parsed = valorDeRed(red.key).safeParse(red.url);
            if (parsed.success)
                return;
            // `path: [i, 'url']` queda como `redes.i.url`, que es lo que el panel lee para pintar el
            // error en la fila correcta. Cambiarlo deja los mensajes sin dueño en la pantalla.
            for (const issue of parsed.error.issues) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, 'url'], message: issue.message });
            }
        });
    })
        .refine((redes) => new Set(redes.map((r) => r.key)).size === redes.length, {
        message: 'Hay una red repetida',
    })
        // Lo que sale es lo que se guarda, y lo que se guarda es lo que va en el `href`: acá el valor
        // pasa por el validador de su clave, que además **normaliza** (el correo sale con `mailto:`
        // aunque haya entrado pelado). Un `superRefine` no puede transformar, de ahí que la validación
        // y la normalización estén en dos pasos sobre la misma lista.
        //
        // `safeParse` y no `parse`: zod corre el `transform` **igual** cuando un refinamiento anterior
        // falló (el estado queda "dirty", no "aborted"), así que un `parse` acá tiraría una excepción
        // en vez de dejar que `safeParse` devuelva el error de validación. Si no valida, se deja el
        // valor como vino: el parseo ya está condenado a fallar.
        .transform((redes) => redes.map((red) => {
        const parsed = valorDeRed(red.key).safeParse(red.url);
        return parsed.success ? { ...red, url: parsed.data } : red;
    })),
})
    .strict();
export const REDES_ERROR_CODES = {
    /** La `key` no está en el catálogo global. */
    redNoEncontrada: 'RED_NO_ENCONTRADA',
};
